import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import Tree, { TreeNode } from "./Tree/Tree";
import { TextField, PrimaryButton, OverflowSet, IOverflowSetItemProps, ActionButton, Icon, DefaultButton, Link, IconButton, IButtonStyles, TooltipHost, CommandBarButton, Checkbox, Dialog, DialogType, DialogFooter, DatePicker, ComboBox, IComboBoxOption } from "@fluentui/react";
import DataTable from 'react-data-table-component';
import { userAPI } from "../../UserAPI";
import { useAtom } from "jotai";
import { authenticationAtom } from "../../State";
import { AgentToolset, CloudFile, CloudFolder, from, KnowledgeBase, MarketplaceOffer } from "@the-real-insight/tri-model";
import { useModalContext } from "../../components/ModalProvider";
import { useTranslation } from "react-i18next";
import 'react-medium-image-zoom/dist/styles.css';
import PlanPanel from '../PlanPanel';
import MarketingDetails from '../MarketingDetails';
import LoadingIndicator from "../../components/LoadingIndicator";
import { useTestDialog } from "../hooks/useTestDialog";
import SampleRequests from "../SampleRequests";

// Add global style override
const monacoStyle = `
  .monaco-editor * {
    font-family: monospace !important;
    font-weight: bold !important;
    font-size: 12px !important;
  }
`;

export default function RAGManagement() {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showDeleteDialog } = useModalContext() as any;
    const { TestDialog, openTestDialog, closeTestDialog } = useTestDialog();
    let [data, setData] = useState<TreeNode[]>([]);
    const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
    const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<any>();
    const [selectedKey, setSelectedKey] = useState<any>('general');
    const [selectedFile, setSelectedFile] = useState<any>();
    const [linkDialogOpen, setLinkDialogOpen] = useState<boolean>(false);
    const [linkUrl, setLinkUrl] = useState<string>('');
    const [loadingKnowledgeBases, setLoadingKnowledgeBases] = useState(false);
    const isUpdating = useRef(false);
    const onRenderItemStyles = {
        root: { padding: '10px' },
    };

    const knowledgeBaseColumns = [{
        name: '',
        width: '150px',
        cell: (knowledgeBase: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>
            <TooltipHost content="Bearbeiten">
                <ActionButton iconProps={{ iconName: 'Edit' }} onClick={() => setSelectedKnowledgeBase(knowledgeBase)}></ActionButton>
            </TooltipHost>
            <TooltipHost content="Testen">
                <ActionButton iconProps={{ iconName: 'Rocket' }} onClick={() => openTestDialog(knowledgeBase)}></ActionButton>
            </TooltipHost>
            <TooltipHost content="Löschen">
                <ActionButton iconProps={{ iconName: 'Delete' }} styles={{ icon: { color: 'red' } }} onClick={() => {
                    showDeleteDialog(`Wissensbasis ${knowledgeBase.name} löschen`,
                        knowledgeBase.name,
                        async () => {
                            await userAPI.deleteMarketplaceOffer(authentication.customer, knowledgeBase);
                        });
                }}></ActionButton>
            </TooltipHost>
        </div>
    }, {
        key: "name",
        name: "Name",
        fieldName: "name",
        width: "300px",
        cell: (row: any) => <div>{row.name}</div>
    }, {
        width: '200px',
        name: 'Intern verfügbar',
        cell: (knowledgeBase: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>{knowledgeBase.published ?
            <div><Icon iconName='CheckMark' style={{ color: 'green' }} /></div>
            :
            <div><Icon iconName='Cancel' style={{ color: 'red' }} /></div>
        }</div>
    }, {
        width: '200px',
        name: 'Im Marktplatz angeboten',
        cell: (knowledgeBase: any) => <div className='flexGrow1 displayFlex justifyContentCenter'>{knowledgeBase.offered ?
            <div><Icon iconName='CheckMark' style={{ color: 'green' }} /></div>
            :
            <div><Icon iconName='Cancel' style={{ color: 'red' }} /></div>
        }</div>
    }, {
        key: "description",
        name: "Beschreibung",
        fieldName: "description",
        width: "400px",
        cell: (row: any) => <div className="textS marginTopXS marginBottomXS">{row.synopsis}</div>
    }
    ];
    const linkColumns = [{
        name: 'Aktionen',
        width: '150px',
        cell: (link: any) => <div>
            <ActionButton iconProps={{ iconName: 'Delete' }} onClick={() => {
                const newKnowledgeBase = new KnowledgeBase({ ...selectedKnowledgeBase });
                newKnowledgeBase.links = newKnowledgeBase.links.filter((l: any) => l.url !== link.url);
                setSelectedKnowledgeBase(newKnowledgeBase);
            }} />
        </div>
    }, {
        name: 'URL',
        width: '400px',
        cell: (link: any) => <div>{link.url}</div>
    }, {
        name: 'Depth',
        width: '150px',
        cell: (link: any) => <div>{link.depth}</div>
    }];

    const handleClick = (props: any) => {
        // do extra stuff with this data
        // {state, id, name, type, path?, parent?, level? }
        // console.log(props);
    };
    const handleUpdate = (state: any) => {
        localStorage.setItem(
            "tree",
            JSON.stringify(state, function (key, value) {
                if (key === "parent" || key === "id") {
                    return null;
                }
                return value;
            })
        );
    };

    const toTreeNodes = (folders: CloudFolder[], knowledgeBase?: any): TreeNode[] => {
        let knowledgeBaseFiles: any = {};

        // To map status of files and folders in knowledgebase

        if (knowledgeBase && knowledgeBase.files) {
            knowledgeBaseFiles = knowledgeBase.files.reduce((acc: any, file: any) => {
                acc[file.path] = file;

                return acc;
            }, {});
        }

        // TODO Filter is a hack to remove the root folder
        const treeNodes: TreeNode[] = folders/*.filter((folder: CloudFolder) => {
            return folder.name() && folder.name().trim().length > 0;
        })*/.map((folder: CloudFolder) => {
            return {
                type: 'folder',
                name: folder.isRoot() ? 'Managed Documents' : folder.name(),
                files: [...toTreeNodes(folder.folders(), knowledgeBase),
                ...folder.files().map((file: CloudFile) => {
                    return {
                        type: 'file' as const,
                        name: file.name(),
                        selected: !!knowledgeBaseFiles[file.path()],
                        processing: knowledgeBaseFiles[file.path()] ? knowledgeBaseFiles[file.path()].processing : false,
                        onSelect: (checked: boolean) => {
                            let newKnowledgeBase = new KnowledgeBase({ ...knowledgeBase });;

                            if (checked) {
                                newKnowledgeBase.addFile(file.path());

                            } else {
                                newKnowledgeBase.removeFile(file.path());
                            }

                            console.log('newKnowledgeBase', newKnowledgeBase);

                            userAPI.updateMarketplaceOffer(authentication.customer, newKnowledgeBase);
                            setSelectedKnowledgeBase(newKnowledgeBase);
                        },
                        onClick: () => {
                            setSelectedFile(file);
                        },
                        onDelete: async (deleteCallback: () => void) => {
                            showDeleteDialog(`Datei ${file.name()} löschen`,
                                file.name(),
                                async () => {
                                    console.log('File deleted', file.path());
                                    await userAPI.deleteFile(authentication.customer, file.path());
                                    deleteCallback();
                                });
                        },
                        onRename: async (name: string) => {
                            const rootPath = file.path().split('/').slice(0, -1).join('/');
                            console.log(`Rename file ${file.path()}`, `${rootPath}/${name}`);
                            await userAPI.renameFile(authentication.customer, file.path(), name);
                            updateStorage();
                        },
                        onDownload: async () => {
                            console.log(`Download file ${file.path()}`);

                            try {
                                const { blob, contentType, filename } = await userAPI.downloadFile(authentication.customer, file.path());

                                // Create a blob with the correct type
                                const typedBlob = new Blob([blob], { type: contentType || 'application/octet-stream' });
                                const url = URL.createObjectURL(typedBlob);

                                // Create a temporary link element
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = filename;

                                // Append to body, click, and remove
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);

                                // Clean up the URL object after a delay
                                setTimeout(() => URL.revokeObjectURL(url), 100);
                            } catch (error) {
                                console.error('Error downloading file:', error);
                                pushError('Fehler beim Herunterladen der Datei');
                            }
                        }
                    }
                })],
                //selected: folder.files().some((file: CloudFile) => !!knowledgeBaseFiles[file.path()]),
                onSelect: (checked: boolean) => {
                    console.log('Folder checked', folder.path(), checked);
                },
                onDelete: async (deleteCallback: () => void) => {
                    await showDeleteDialog(
                        `Ordner ${folder.name()} löschen`,
                        folder.name(),
                        async () => {
                            console.log('Folder deleted', folder.path());
                            await userAPI.deleteFolder(authentication.customer, folder.path());
                            deleteCallback();
                        });
                },
                onCreateFolder: async (name: string, createCallback: () => void) => {
                    console.log(`Create folder in ${folder.path()}`, name);
                    await userAPI.addFolder(authentication.customer, folder.path(), name);
                    createCallback();
                },
                onCreateFile: async (name: string, createCallback: () => void) => {
                    console.log(`Create file in ${folder.path()}`, name);
                    createCallback();
                },
                onRename: async (name: string) => {
                    const rootPath = folder.path().split('/').slice(0, -1).join('/');
                    console.log(`Rename folder ${folder.path()}`, `${rootPath}/${name}`);
                    await userAPI.renameFolder(authentication.customer, folder.path(), name);
                    updateStorage();
                }
            }
        });

        return treeNodes;
    };

    const updateStorage = async () => {
        const storage = await userAPI.getStorage(authentication.customer);
        const treeNodes = toTreeNodes([storage.rootFolder()], selectedKnowledgeBase);

        setData(treeNodes);
    }

    const loadKnowledgeBases = async () => {
        try {
            setLoadingKnowledgeBases(true);

            const knowledgeBases = await userAPI.query(from(KnowledgeBase.type));

            setKnowledgeBases(knowledgeBases);
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingKnowledgeBases(false);
        }
    }

    useEffect(() => {
        if (!selectedKnowledgeBase) {
            loadKnowledgeBases();
        } else {
            updateStorage();
        }
    }, [selectedKnowledgeBase]);

    const menuItems = [
        {
            key: 'general',
            icon: 'Global',
            name: 'Allgemeine Informationen',
            title: 'Add',
            onClick: () => setSelectedKey('general'),
        },
        {
            key: 'documents',
            icon: 'Documents',
            name: 'Dokumente',
            title: 'Dokumente',
            onClick: () => setSelectedKey('documents'),
        },
        {
            key: 'links',
            icon: 'Link',
            name: 'Links',
            title: 'Links',
            onClick: () => setSelectedKey('links'),
        },
        {
            key: 'sampleRequests',
            icon: 'SampleRequests',
            name: 'Beispielanfragen',
            title: 'Beispielanfragen',
            onClick: () => setSelectedKey('sampleRequests'),
        },
        {
            key: 'details',
            icon: 'Details',
            name: 'Angebotsdetails',
            title: 'Angebotsdetails',
            onClick: () => setSelectedKey('details'),
        },
        {
            key: 'billing',
            icon: 'Billing',
            name: 'Abrechnung',
            title: 'Abrechnung',
            onClick: () => setSelectedKey('billing'),
        }
    ];

    const onImagesChange = async (imageList: any, addUpdateIndex: any) => {
        try {
            // TODO put in API function

            const form = new FormData();

            form.append("file", imageList[0].file);

            const image = await userAPI.uploadFile(form);

            setSelectedKnowledgeBase({ ...selectedKnowledgeBase, images: [...selectedKnowledgeBase.images, image.path] });
        } catch (error: any) {
            pushError(error);
        }
    };

    return <><div>
        <style>{monacoStyle}</style>
        {selectedKnowledgeBase
            ?
            <div>
                <div className="displayFlex alignItemsCenter">
                    <div>
                        <div className="textS">Wissensbasis</div>
                        <div className="textHeading">{selectedKnowledgeBase.name}</div>
                    </div>
                    <div className="flexGrow1 displayFlex justifyContentEnd gapM">
                        <DefaultButton className="marginTopM" onClick={() => {
                            setSelectedKnowledgeBase(null);
                        }}>Abbrechen</DefaultButton>
                        {selectedKnowledgeBase._id ?
                            <PrimaryButton className="marginTopM" onClick={async () => {
                                await userAPI.updateMarketplaceOffer(authentication.customer, selectedKnowledgeBase);
                                setSelectedKnowledgeBase(null);
                            }}>Speichern</PrimaryButton>
                            :
                            <PrimaryButton className="marginTopM" onClick={async () => {
                                await userAPI.createMarketplaceOffer(authentication.customer, selectedKnowledgeBase);
                                setSelectedKnowledgeBase(null);
                            }}>Erstellen</PrimaryButton>
                        }
                    </div>
                </div>
                <div className="marginTopM displayFlex gapM alignItemsBaseline">
                    <Checkbox label="Intern verfügbar" checked={selectedKnowledgeBase.published} onChange={(e, value) => {
                        const newKnowledgeBase = new KnowledgeBase({ ...selectedKnowledgeBase, published: value });
                        setSelectedKnowledgeBase(newKnowledgeBase);
                    }} />
                    <Checkbox label="Im Marktplatz angeboten" checked={selectedKnowledgeBase.offered} onChange={(e, value) => {
                        const newKnowledgeBase = new KnowledgeBase({ ...selectedKnowledgeBase, offered: value });
                        setSelectedKnowledgeBase(newKnowledgeBase);
                    }} />
                    <ActionButton className='marginTopM' iconProps={{ iconName: 'Launch' }} onClick={async () => {
                        await userAPI.publishMarketplaceOffer(authentication.customer, selectedKnowledgeBase);
                    }}> Im Marktplatz veröffentlichen</ActionButton>
                </div>
                <div className="marginTopL width1000 displayFlex gapM">
                    <div style={{ minWidth: '200px', maxWidth: '200px' }}>
                        {menuItems.map((item) => (
                            <div key={item.key} className={`paddingS displayFlex alignItemsCenter hoverHand ${selectedKey === item.key ? 'backgroundColorLightestGrey' : ''}`} onClick={() => setSelectedKey(item.key)}>
                                {/* <Icon iconName={item.icon} /> */}
                                <div>{item.name}</div>
                            </div>
                        ))}
                    </div>
                    <div>
                        {(() => {
                            switch (selectedKey) {
                                case 'general':
                                    return (
                                        <div className="width600">
                                            <div>
                                                <TextField
                                                    label="Name der Wissensbasis"
                                                    value={selectedKnowledgeBase.name}
                                                    onChange={(e, value) => {
                                                        setSelectedKnowledgeBase({ ...selectedKnowledgeBase, name: value });
                                                    }}
                                                />
                                            </div>
                                            <div className="marginTopS">
                                                <TextField
                                                    label="Beschreibung der Wissensbasis"
                                                    value={selectedKnowledgeBase.synopsis}
                                                    multiline
                                                    rows={5}
                                                    onChange={(e, value) => {
                                                        setSelectedKnowledgeBase({ ...selectedKnowledgeBase, synopsis: value });
                                                    }}
                                                />
                                            </div>
                                            <div className="marginTopS textS colorPrimary">Diese Kurzbeschreibung wird von der AI verwendet, um deine Wissensbasis weiter zu durchsuchen, wenn sie zur Frage eines Nutzers passt.</div>
                                        </div>
                                    );
                                case 'details':
                                    return (
                                        <MarketingDetails
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedKnowledgeBase}
                                            onImagesChange={onImagesChange}
                                        />
                                    );
                                case 'documents':
                                    return (
                                        <>
                                            <div className="marginTopM textM">Wähle Verzeichnisse und/oder Dateien aus deinem Dokumentenbestand aus, die in diesem Wissensangebot verwendet werden sollen.</div>
                                            <div className="marginTopS textM">Wenn erforderlich kannst du auch neue Verzeichnisse erstellen oder Dateien per Drag & Drop hinzufügen.</div>
                                            <div className="marginTopM displayFlex gapM">
                                                <div className="borderNeutral paddingM width600 height400 overflowYScroll overflowXScroll backgroundColorWhite">
                                                    <Tree data={data} onUpdate={handleUpdate} onNodeClick={handleClick} />
                                                </div>
                                                <div className="borderNeutral paddingM width400 backgroundColorWhite">
                                                    {selectedFile ?
                                                        <div>
                                                            <div className="textM">
                                                                <div className="textSubHeading">{selectedFile.path().split('/').pop()}</div>
                                                            </div>
                                                            <div className="marginTopS">
                                                                <Checkbox label="Shared" checked={selectedFile.getTag('shared') === 'true'} onChange={async (e, value) => {
                                                                    const newSelectedFile = new CloudFile(selectedFile._name, selectedFile._path, selectedFile.tags);

                                                                    newSelectedFile.setTag('shared', value ? 'true' : 'false');

                                                                    if (value) {
                                                                        newSelectedFile.setTag('link', '');
                                                                    }

                                                                    setSelectedFile(newSelectedFile);

                                                                    await userAPI.updateTags(authentication.customer, newSelectedFile.path(), newSelectedFile.tags);
                                                                    updateStorage();
                                                                }} />
                                                            </div>
                                                            <div className="marginTopS">
                                                                <TextField
                                                                    label="Link"
                                                                    disabled={selectedFile.getTag('shared') === 'true'}
                                                                    value={selectedFile.getTag('link')}
                                                                    onChange={(e, value) => {
                                                                        const newSelectedFile = new CloudFile(selectedFile._name, selectedFile._path, selectedFile.tags);
                                                                        newSelectedFile.setTag('link', value || '');
                                                                        setSelectedFile(newSelectedFile);
                                                                    }}
                                                                    onBlur={async () => {
                                                                        await userAPI.updateTags(authentication.customer, selectedFile.path(), selectedFile.tags);
                                                                        updateStorage();
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="marginTopS">
                                                                <TextField
                                                                    label="Quellenangabe"
                                                                    multiline
                                                                    rows={5}
                                                                    value={selectedFile.getTag('source')}
                                                                    onChange={(e, value) => {
                                                                        const newSelectedFile = new CloudFile(selectedFile._name, selectedFile._path, selectedFile.tags);
                                                                        newSelectedFile.setTag('source', value || '');
                                                                        setSelectedFile(newSelectedFile);
                                                                    }}
                                                                    onBlur={async () => {
                                                                        await userAPI.updateTags(authentication.customer, selectedFile.path(), selectedFile.tags);
                                                                        updateStorage();
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        :
                                                        <div className="flexGrow1 displayFlex flexDirectionColumn alignItemsCenter justifyContentCenter textM" style={{ height: '100%' }}>Keine Datei ausgewählt</div>
                                                    }
                                                </div>
                                            </div>
                                        </>
                                    );
                                case 'links':
                                    return (
                                        <>
                                            <div className="marginTopM textM">Wähle relevante Links aus, die in diesem Wissensangebot verwendet werden sollen.</div>
                                            <ActionButton className='marginTopM' iconProps={{ iconName: 'Add' }} onClick={async () => {
                                                setLinkDialogOpen(true);
                                            }}> Link hinzufügen</ActionButton>
                                            <div className='marginTopM borderNeutral'>
                                                <DataTable data={selectedKnowledgeBase.links}
                                                    columns={linkColumns}
                                                    selectableRows
                                                    selectableRowsSingle
                                                    onSelectedRowsChange={({ selectedRows }) => {
                                                    }}
                                                    pagination
                                                />
                                            </div>
                                        </>
                                    );
                                case 'sampleRequests':
                                    return (
                                        <SampleRequests
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedKnowledgeBase}
                                        />
                                    );
                                case 'billing':
                                    return (
                                        <PlanPanel
                                            type={KnowledgeBase.type}
                                            marketplaceOffer={selectedKnowledgeBase as MarketplaceOffer}
                                            setMarketplaceOffer={setSelectedKnowledgeBase}
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })()}
                    </div>
                </div>
            </div>
            :
            <div>
                <ActionButton className='marginTopM' iconProps={{ iconName: 'Add' }} onClick={async () => {
                    const newKnowledgeBase = new KnowledgeBase({
                        name: 'Neue Wissensbasis',
                        synopsis: '',
                        description: '',
                        marketingContent: '',
                        urlTermsAndConditions: '',
                        urlPrivacyPolicy: '',
                        files: [],
                        getTypeClass: 'KnowledgeBase',
                        getTypeName: 'KnowledgeBase'
                    });
                    setSelectedKnowledgeBase(newKnowledgeBase);
                }}> Wissensbasis hinzufügen</ActionButton>
                <div className='marginTopM borderNeutral'>
                    <DataTable data={knowledgeBases}
                        columns={knowledgeBaseColumns}
                        pagination
                        noDataComponent={loadingKnowledgeBases ? <LoadingIndicator loading={true} text={'Lade Wissensbasen...'}></LoadingIndicator> : <div className='paddingL'>Keine Wissensbasen gefunden</div>}
                    />
                </div>
            </div>
        }
    </div>
        <TestDialog />
        <Dialog
            hidden={!linkDialogOpen}
            onDismiss={() => setLinkDialogOpen(false)}
            maxWidth={1000}
            minWidth={800}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Link hinzufügen',
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            <div>
                <TextField label="URL" value={linkUrl} onChange={(e, value) => {
                    setLinkUrl(value || '');
                }} />
            </div>
            <DialogFooter className="marginTopXXL">
                <DefaultButton onClick={() => {
                    setLinkDialogOpen(false);
                }} text={t('global.close')} />
                <PrimaryButton onClick={() => {
                    const newKnowledgeBase = new KnowledgeBase({ ...selectedKnowledgeBase });
                    newKnowledgeBase.links.push({ url: linkUrl, depth: 1 });
                    setSelectedKnowledgeBase(newKnowledgeBase);
                    setLinkDialogOpen(false);
                }} text={t('global.create')} />
            </DialogFooter>
        </Dialog>
    </>;
}
